import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import mammoth from 'mammoth';
import { axiosInstance } from '@edison/api-client-react';

interface UploadZoneProps {
  onUploadStarted: (artefactId: string) => void;
}

const ACCEPTED_TYPES = ['.vtt', '.txt', '.md', '.docx'];
// Extensions we read directly as UTF-8 text. .docx is extracted separately via mammoth.
const TEXT_TYPES = ['.vtt', '.txt', '.md'];
const SUPPORTED_HINT = '.txt, .md, .vtt or .docx';
const MAX_SIZE_MB = 10;

function extractErrorMessage(err: unknown): string | null {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message;
  return typeof message === 'string' && message.length > 0 ? message : null;
}

export function UploadZone({ onUploadStarted }: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function processFile(file: File) {
    setError(null);
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File too large — maximum is ${MAX_SIZE_MB}MB`);
      return;
    }

    // Drag-and-drop bypasses the input's `accept` filter, so re-check the extension here.
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (ext !== '.docx' && !TEXT_TYPES.includes(ext)) {
      setError(`Unsupported file type. Upload ${SUPPORTED_HINT}.`);
      return;
    }

    setUploading(true);

    // Extract text per file type: .docx via mammoth (it is a ZIP, never read as text),
    // everything else as UTF-8.
    let content: string;
    try {
      if (ext === '.docx') {
        const arrayBuffer = await file.arrayBuffer();
        content = (await mammoth.extractRawText({ arrayBuffer })).value;
      } else {
        content = await file.text();
      }
    } catch {
      setError(`Could not read this file. Upload ${SUPPORTED_HINT}.`);
      setUploading(false);
      return;
    }

    // Defensive: a corrupt or renamed binary can still yield NUL (\u0000) bytes, which
    // PostgreSQL rejects. Catch it here so we never send an unstorable request.
    if (content.includes('\u0000')) {
      setError(
        `This file appears to be binary. Upload ${SUPPORTED_HINT} — the evidence page extracts document text automatically.`,
      );
      setUploading(false);
      return;
    }

    try {
      const res = await axiosInstance.post<{ data: { id: string } }>('/api/artefacts/upload-file', {
        content,
        filename: file.name,
        type: 'meeting_transcript',
      });
      onUploadStarted(res.data.data.id);
    } catch (err) {
      setError(extractErrorMessage(err) ?? 'Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void processFile(file);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void processFile(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload a transcript or evidence file"
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => !uploading && inputRef.current?.click()}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !uploading) inputRef.current?.click(); }}
      className={[
        'border-2 border-dashed rounded-xl px-6 py-5 text-center cursor-pointer transition-colors select-none',
        isDragging
          ? 'border-[oklch(0.55_0.18_270)] bg-[oklch(0.55_0.18_270/0.05)]'
          : 'border-hairline hover:border-[oklch(0.70_0.01_250)]',
        uploading ? 'pointer-events-none opacity-60' : '',
      ].join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={handleFileInput}
        aria-hidden="true"
      />
      <Upload className="mx-auto mb-2 h-5 w-5 text-dim" aria-hidden="true" />
      {uploading ? (
        <p className="text-sm text-dim">Uploading…</p>
      ) : (
        <>
          <p className="text-[13px] text-ink">
            Drop a transcript or evidence file, or{' '}
            <span className="text-[oklch(0.55_0.18_270)] underline">browse</span>
          </p>
          <p className="mt-1 text-[11.5px] text-dim">
            {ACCEPTED_TYPES.join(' ')} · Processes in background · Max {MAX_SIZE_MB}MB
          </p>
        </>
      )}
      {error && <p className="mt-2 text-[11.5px] text-destructive">{error}</p>}
    </div>
  );
}
